import {
  InternalError,
  PublicError,
  TaggedError,
} from "@/domain/errors/common";

const NETWORK_ERROR_MESSAGES = {
  400: "Solicitud incorrecta. Por favor revise los datos enviados",
  401: "No autorizado. Verifique sus credenciales",
  403: "Acceso denegado. No tiene permisos para realizar esta acción",
  404: "Recurso no encontrado",
  500: "Error interno del servidor. Por favor, inténtelo de nuevo más tarde",
  999: "Error de red desconocido. Por favor, inténtelo de nuevo más tarde",
} as const;

function getHandledStatusCode(status: number): HandledStatusCode {
  const DEFAULT_STATUS = 999 as const;
  if (isNaN(status)) {
    return DEFAULT_STATUS;
  }
  if (status < 400 || status > 599) {
    return DEFAULT_STATUS;
  }

  if (!NETWORK_ERROR_MESSAGES[status as HandledStatusCode]) {
    return DEFAULT_STATUS;
  }

  return status as HandledStatusCode;
}

type HandledStatusCode = keyof typeof NETWORK_ERROR_MESSAGES;

/**
 * Custom tagged network error classes for handling specific HTTP status codes.
 * These errors extend the NetworkError class to provide
 * meaningful messages based on the HTTP status code.
 *
 * @class NetworkError
 */
class TaggedNetworkError extends TaggedError("TaggedNetworkError") {
  public readonly status: number;
  constructor(status: HandledStatusCode, message: string) {
    super({ message, cause: undefined });
    this.status = status;
  }
}

class BadRequestNetworkError extends InternalError {
  constructor() {
    super(NETWORK_ERROR_MESSAGES[400]);
  }
}

class UnauthorizedNetworkError extends PublicError {
  constructor() {
    super(NETWORK_ERROR_MESSAGES[401]);
  }
}

class ForbiddenNetworkError extends PublicError {
  constructor() {
    super(NETWORK_ERROR_MESSAGES[403]);
  }
}

class NotFoundNetworkError extends InternalError {
  constructor() {
    super(NETWORK_ERROR_MESSAGES[404]);
  }
}

class InternalServerNetworkError extends InternalError {
  constructor() {
    super(NETWORK_ERROR_MESSAGES[500]);
  }
}

class TaggedBadRequestNetworkError extends TaggedNetworkError {
  override readonly status = 400;
  constructor(customMessage?: string) {
    super(400, customMessage ?? NETWORK_ERROR_MESSAGES[400]);
  }
}
class TaggedUnauthorizedNetworkError extends TaggedNetworkError {
  override readonly status = 401;
  constructor(customMessage?: string) {
    super(401, customMessage ?? NETWORK_ERROR_MESSAGES[401]);
  }
}

class TaggedForbiddenNetworkError extends TaggedNetworkError {
  override readonly status = 403;
  constructor(customMessage?: string) {
    super(403, customMessage ?? NETWORK_ERROR_MESSAGES[403]);
  }
}

class TaggedNotFoundNetworkError extends TaggedNetworkError {
  override readonly status = 404;
  constructor(customMessage?: string) {
    super(404, customMessage ?? NETWORK_ERROR_MESSAGES[404]);
  }
}
class TaggedInternalServerNetworkError extends TaggedNetworkError {
  override readonly status = 500;
  constructor(customMessage?: string) {
    super(500, customMessage ?? NETWORK_ERROR_MESSAGES[500]);
  }
}

class TaggedUnknownNetworkError extends TaggedNetworkError {
  override readonly status = 999;
  constructor(customMessage?: string) {
    super(999, customMessage ?? NETWORK_ERROR_MESSAGES[999]);
  }
}

function createNetworkError(
  status: number,
  customMessages?: Partial<Record<HandledStatusCode, string>>
): TaggedNetworkError {
  const handledStatus = getHandledStatusCode(status);
  switch (handledStatus) {
    case 400:
      return new TaggedBadRequestNetworkError(customMessages?.[400]);
    case 401:
      return new TaggedUnauthorizedNetworkError(customMessages?.[401]);
    case 403:
      return new TaggedForbiddenNetworkError(customMessages?.[403]);
    case 404:
      return new TaggedNotFoundNetworkError(customMessages?.[404]);
    case 500:
      return new TaggedInternalServerNetworkError(customMessages?.[500]);
    default:
      return new TaggedUnknownNetworkError(customMessages?.[999]);
  }
}

function isTaggedNetworkError(error: Error): error is TaggedNetworkError {
  return error instanceof TaggedNetworkError;
}

/**
 * Exports custom tagged network error classes for handling specific HTTP status codes.
 * These errors extend the NetworkError class to provide
 * meaningful messages based on the HTTP status code.
 *
 * @module NetworkErrors
 */

export {
  BadRequestNetworkError,
  ForbiddenNetworkError,
  InternalServerNetworkError,
  NotFoundNetworkError,
  UnauthorizedNetworkError,
};

export {
  TaggedBadRequestNetworkError,
  TaggedForbiddenNetworkError,
  TaggedInternalServerNetworkError,
  TaggedNotFoundNetworkError,
  TaggedUnauthorizedNetworkError,
  TaggedUnknownNetworkError,
  TaggedNetworkError,
  createNetworkError,
  isTaggedNetworkError,
};

export type { HandledStatusCode };
